import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * AdInterstitial Component
 * Shows full-screen overlay ad (like interstitial)
 * 
 * Props:
 * - show: Boolean to control visibility
 * - onClose: Callback when user closes ad
 * - adSlot: Your ad slot ID from AdSense
 * - autoCloseDelay: Auto-close after X seconds (0 = manual close only)
 */
const AdInterstitial = ({ 
  show, 
  onClose, 
  adSlot = '9876543210',
  autoCloseDelay = 5 // Auto-close after 5 seconds
}) => {
  const [countdown, setCountdown] = useState(autoCloseDelay);

  useEffect(() => {
    if (show && autoCloseDelay > 0) {
      setCountdown(autoCloseDelay);
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            onClose();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [show, autoCloseDelay, onClose]);

  useEffect(() => {
    if (show) {
      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      } catch (e) {
        console.error('AdSense error:', e);
      }
    }
  }, [show]);

  if (!show) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-2xl p-6 max-w-2xl w-full mx-4 relative"
        >
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
            data-testid="close-interstitial"
          >
            <X size={20} />
          </button>

          {/* Countdown */}
          {countdown > 0 && (
            <div className="text-center text-sm text-gray-500 mb-4">
              Closing in {countdown}s...
            </div>
          )}

          {/* Ad */}
          <div className="text-center text-xs text-gray-400 mb-4">Advertisement</div>
          <ins
            className="adsbygoogle"
            style={{ display: 'block', minHeight: '250px' }}
            data-ad-client="ca-pub-9789426917966477" // TODO: Replace with your AdSense publisher ID
            data-ad-slot={adSlot}
            data-ad-format="fluid"
            data-ad-layout="in-article"
          />
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AdInterstitial;